import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, FileText, Loader2, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import { quotesApi } from '../api';
import { centsToDollarString, fmtDate } from '../constants';
import { QUOTE_STATUS_OPTIONS, QUOTE_STATUS_STYLES, QUOTE_SORT_OPTIONS, quoteStatusLabel, customerActivity } from '../quoteConstants';
import { QuoteFormDialog } from '../components/QuoteFormDialog';

const selectCls = 'bg-[#1A1A1A] border border-gray-700 text-white rounded-md px-3 py-2 text-sm focus:border-[#D4AF37] focus:outline-none';
const inputCls = 'bg-[#1A1A1A] border-gray-700 text-white focus:border-[#D4AF37]';

const money = (c) => (c == null ? <span className="text-gray-500">—</span> : centsToDollarString(c));

const StatusBadge = ({ q }) => (
  <span className="inline-flex items-center gap-1.5">
    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border whitespace-nowrap ${QUOTE_STATUS_STYLES[q.status]}`}>{quoteStatusLabel(q.status)}</span>
    {q.is_expired && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border bg-amber-900/30 text-amber-400 border-amber-800">Expired</span>}
    {q.converted_project && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40" data-testid={`quote-converted-badge-${q.id}`}>Converted</span>}
  </span>
);

export default function OpsQuotes() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({ search: '', status: '', expired: '', archived: false, sort: 'recently_updated' });
  const [formOpen, setFormOpen] = useState(false);

  const loadSummary = useCallback(() => {
    quotesApi.list({}).then((r) => {
      const all = r.data;
      const accepted = all.filter((q) => q.status === 'accepted');
      setSummary({
        draft: all.filter((q) => q.status === 'draft').length,
        sent: all.filter((q) => q.status === 'sent').length,
        accepted: accepted.length,
        acceptedValue: accepted.reduce((s, q) => s + (q.totals.customer_subtotal_cents || 0), 0),
      });
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const p = { archived: f.archived, sort: f.sort };
    if (f.search.trim()) p.search = f.search.trim();
    if (f.status) p.status = f.status;
    if (f.expired) p.expired = f.expired === 'yes';
    quotesApi.list(p).then((r) => setQuotes(r.data)).catch(() => toast.error('Could not load quotes')).finally(() => setLoading(false));
  }, [f]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="ops-quotes-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-white">Quotes</h2>
          <p className="text-gray-400 mt-1">Build professional quotes from your Price Book and send customers a secure approval link.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline"
            onClick={async () => {
              const url = `${window.location.origin}/quote`;
              try {
                await navigator.clipboard.writeText(url);
                toast.success('Customer quote link copied — send it by text or email');
              } catch {
                window.prompt('Copy this link:', url);
              }
            }}
            className="border-[#D4AF37]/50 text-[#D4AF37] hover:text-[#B8941F]" data-testid="customer-quote-link-btn">
            <Link2 size={16} /> Customer Quote Link
          </Button>
          <Button onClick={() => setFormOpen(true)} className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold" data-testid="new-quote-btn">
            <Plus size={16} /> New Quote
          </Button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <Card className="bg-[#111111] border-gray-800 p-4" data-testid="quotes-sum-draft"><div className="text-xs text-gray-500">Draft</div><div className="text-xl font-bold text-white mt-1">{summary.draft}</div></Card>
          <Card className="bg-[#111111] border-gray-800 p-4" data-testid="quotes-sum-sent"><div className="text-xs text-gray-500">Sent / Awaiting Response</div><div className="text-xl font-bold text-blue-400 mt-1">{summary.sent}</div></Card>
          <Card className="bg-[#111111] border-gray-800 p-4" data-testid="quotes-sum-accepted"><div className="text-xs text-gray-500">Accepted</div><div className="text-xl font-bold text-green-400 mt-1">{summary.accepted}</div></Card>
          <Card className="bg-[#111111] border-gray-800 p-4" data-testid="quotes-sum-accepted-value"><div className="text-xs text-gray-500">Total Accepted Value</div><div className="text-xl font-bold text-[#D4AF37] mt-1">{centsToDollarString(summary.acceptedValue)}</div></Card>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <Input value={f.search} onChange={(e) => setF((x) => ({ ...x, search: e.target.value }))} placeholder="Search quotes…" className={`${inputCls} w-full sm:w-56`} data-testid="quotes-search" />
        <select value={f.status} onChange={(e) => setF((x) => ({ ...x, status: e.target.value }))} className={selectCls} data-testid="quotes-status-filter">
          <option value="">All statuses</option>
          {QUOTE_STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={f.expired} onChange={(e) => setF((x) => ({ ...x, expired: e.target.value }))} className={selectCls} data-testid="quotes-expired-filter">
          <option value="">Expired: any</option>
          <option value="yes">Expired only</option>
          <option value="no">Not expired</option>
        </select>
        <select value={f.sort} onChange={(e) => setF((x) => ({ ...x, sort: e.target.value }))} className={selectCls} data-testid="quotes-sort">
          {QUOTE_SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer pb-2">
          <input type="checkbox" checked={f.archived} onChange={(e) => setF((x) => ({ ...x, archived: e.target.checked }))} data-testid="quotes-archived-toggle" /> Archived
        </label>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#D4AF37]" size={28} /></div>
      ) : quotes.length === 0 ? (
        <Card className="bg-[#111111] border-gray-800 p-10 text-center" data-testid="quotes-empty-state">
          <FileText size={36} className="mx-auto text-gray-600 mb-3" />
          <p className="text-gray-400">No quotes yet. Create your first quote to get started.</p>
        </Card>
      ) : (
        <>
          <div className="hidden lg:block overflow-x-auto rounded-lg border border-gray-800">
            <table className="w-full text-sm" data-testid="quotes-table">
              <thead className="bg-black text-[#D4AF37] text-[10px] uppercase tracking-wider font-brand">
                <tr>
                  <th className="text-left px-4 py-3">Quote</th>
                  <th className="text-left px-4 py-3">Customer</th>
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Customer Activity</th>
                  <th className="text-right px-4 py-3">Customer Total</th>
                  <th className="text-left px-4 py-3">Updated</th>
                </tr>
              </thead>
              <tbody>
                {quotes.map((q) => {
                  const act = customerActivity(q);
                  return (
                    <tr key={q.id} onClick={() => navigate(`/ops/quotes/${q.id}`)} className="border-t border-gray-800 hover:bg-[#161616] cursor-pointer" data-testid={`quote-row-${q.id}`}>
                      <td className="px-4 py-3"><div className="text-white font-medium">{q.title}</div><div className="text-xs text-gray-500">{q.quote_number}</div></td>
                      <td className="px-4 py-3 text-gray-300">{q.customer_name}</td>
                      <td className="px-4 py-3 text-gray-400">{fmtDate(q.quote_date)}</td>
                      <td className="px-4 py-3"><StatusBadge q={q} /></td>
                      <td className={`px-4 py-3 text-xs ${act.cls}`}>
                        {act.label}
                        {q.converted_project && (
                          <button onClick={(e) => { e.stopPropagation(); navigate(`/ops/projects/${q.converted_project.id}?tab=quote-change-orders`); }}
                            className="block text-[#D4AF37] hover:underline mt-0.5" data-testid={`open-project-link-${q.id}`}>
                            Open Project
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-white">
                        {q.totals.quote_total_cents == null && q.totals.active_line_count > 0 ? <span className="text-amber-400 text-xs">Incomplete</span> : money(q.totals.quote_total_cents)}
                        {q.totals.discount_percent != null && <span className="block text-[10px] text-[#D4AF37]">{q.totals.discount_percent}% discount</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{fmtDate(q.updated_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="lg:hidden space-y-3">
            {quotes.map((q) => {
              const act = customerActivity(q);
              return (
                <Card key={q.id} onClick={() => navigate(`/ops/quotes/${q.id}`)} className="bg-[#111111] border-gray-800 p-4 cursor-pointer" data-testid={`quote-card-${q.id}`}>
                  <div className="flex justify-between items-start gap-2">
                    <div><div className="text-white font-medium">{q.title}</div><div className="text-xs text-gray-500">{q.quote_number} · {q.customer_name}</div></div>
                    <StatusBadge q={q} />
                  </div>
                  <div className="flex justify-between items-center mt-3 text-sm">
                    <span className={`text-xs ${act.cls}`}>{act.label}</span>
                    <span className="text-white font-medium">{q.totals.customer_subtotal_cents == null && q.totals.active_line_count > 0 ? <span className="text-amber-400 text-xs">Incomplete</span> : money(q.totals.customer_subtotal_cents)}</span>
                  </div>
                </Card>
              );
            })}
          </div>
        </>
      )}

      <QuoteFormDialog open={formOpen} onOpenChange={setFormOpen} quote={null} onSaved={(q) => navigate(`/ops/quotes/${q.id}`)} />
    </div>
  );
}
